import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import StatsModal from "../../components/Common/StatsModal";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import "../Items/ManageItem.css";
import "./Box.css";
import { partyState } from "../../Recoil/atom";
import { useRecoilState } from "recoil";
import api from "../../api";

const typeLabel = (type) =>
  type ? `${type.charAt(0).toUpperCase()}${type.slice(1)}` : "Unknown";
const typeColors = {
  normal: "#94a3b8",
  fire: "#fb7185",
  water: "#38bdf8",
  electric: "#facc15",
  grass: "#4ade80",
  ice: "#7dd3fc",
  fighting: "#f97316",
  poison: "#c084fc",
  ground: "#fbbf24",
  flying: "#60a5fa",
  psychic: "#f472b6",
  bug: "#a3e635",
  rock: "#f59e0b",
  ghost: "#818cf8",
  dragon: "#22d3ee",
  dark: "#64748b",
  steel: "#cbd5f5",
  fairy: "#fda4af",
};
const getTypeColor = (type) =>
  typeColors[String(type || "").toLowerCase()] || "#94a3b8";
const getHpPercent = (mon) => {
  const maxHp = Number(mon.max_hp || 0);
  const currentHp = Number(mon.current_hp || 0);
  if (!maxHp) return 0;
  return Math.max(0, Math.min(100, Math.round((currentHp / maxHp) * 100)));
};
const getHpTone = (percent) => {
  if (percent >= 100) return "full";
  if (percent <= 20) return "low";
  if (percent <= 60) return "mid";
  return "high";
};

const PartyActionsDropdown = ({ row, handlePartyChange, onOpenChange }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => {
    const next = !dropdownOpen;
    setDropdownOpen(next);
    if (typeof onOpenChange === "function") onOpenChange(next);
  };

  const availableSlots = [];
  for (let i = 1; i <= 6; i++) {
    if (i !== row.slot) {
      availableSlots.push(i);
    }
  }

  return (
    <div
      className="party-actions-dropdown"
      style={{ position: "relative", zIndex: dropdownOpen ? 2000 : "auto" }}
    >
      <Dropdown isOpen={dropdownOpen} toggle={toggle}>
        <DropdownToggle caret size="sm" color="secondary">
          Actions
        </DropdownToggle>
        <DropdownMenu className="box-dropdown-menu">
          {availableSlots.map((slot) => (
            <DropdownItem
              key={slot}
              onClick={() => {
                handlePartyChange(row.id, slot);
                toggle();
              }}
            >
              Switch to Slot {slot}
            </DropdownItem>
          ))}
          <DropdownItem divider />
          <DropdownItem
            onClick={() => {
              handlePartyChange(row.id, 0);
              toggle();
            }}
          >
            Send to Box
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

const Box = () => {
  const [pokemonList, setPokemonList] = useState([]);
  const [search, setSearch] = useState("");
  const [partyValue, setPartyValue] = useRecoilState(partyState);
  const [typeFilter, setTypeFilter] = useState("");
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [allPokemonList, setAllPokemonList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [activePokemonId, setActivePokemonId] = useState(null);
  const [openDropdownCardId, setOpenDropdownCardId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [isSearching, setIsSearching] = useState(false);



    // --- new modal state ---
  const [statsModal, setStatsModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const toggleStatsModal = () => setStatsModal(!statsModal);


  const openStatsModal = async (mon) => {
    setModalLoading(true);
    setStatsModal(true);
    try {
      const { data } = await api.get(`/v1/pokemon/${mon.id}`, { withCredentials: true });
      setModalData(data);
    } catch (err) {
      setModalData({ error: err.response?.data?.detail || "Failed to load" });
    } finally {
      setModalLoading(false);
    }
  };

  // Calculate level from EXP (same curve used elsewhere)
  const getLevel = (exp) => {
    const lvl = Math.floor(Math.pow(exp, 1 / 3));
    return Math.pow(lvl + 1, 3) <= exp ? lvl + 1 : lvl;
  };

  const getLevelColor = (level) => {
    if (level < 59) return '#14b8a6'; // teal for <59
    if (level < 100) return '#ef4444'; // red for 59-99
    return '#FFD700'; // gold for 100+
  };

  const updateFavoriteState = (pokemonId, isFavorite) => {
    setPokemonList((prev) =>
      prev.map((mon) =>
        mon.id === pokemonId ? { ...mon, is_favorite: isFavorite ? 1 : 0 } : mon,
      ),
    );
    setAllPokemonList((prev) =>
      prev.map((mon) =>
        mon.id === pokemonId ? { ...mon, is_favorite: isFavorite ? 1 : 0 } : mon,
      ),
    );
  };

  const toggleFavorite = async (mon, event) => {
    event.stopPropagation();
    const nextFavorite = !mon.is_favorite;
    updateFavoriteState(mon.id, nextFavorite);
    try {
      if (nextFavorite) {
        await api.post(
          "/v1/pokemon/favorite",
          { pokemon_id: mon.id },
          { withCredentials: true },
        );
      } else {
        await api.post(
          "/v1/pokemon/unfavorite",
          { pokemon_id: mon.id },
          { withCredentials: true },
        );
      }
    } catch (err) {
      console.error("Failed to toggle favorite", err);
      updateFavoriteState(mon.id, !nextFavorite);
    }
  };

  const sortedPokemonList = useMemo(() => {
    if (!isSearching) return pokemonList;
    return pokemonList
      .map((mon, idx) => ({ mon, idx }))
      .sort((a, b) => {
        const favDiff = (b.mon.is_favorite || 0) - (a.mon.is_favorite || 0);
        return favDiff !== 0 ? favDiff : a.idx - b.idx;
      })
      .map(({ mon }) => mon);
  }, [isSearching, pokemonList]);


  const searchPokemon = (query) => {
    setLoading(true);
    api
      .get("/v1/pokemon/search", {
        params: { query },
        withCredentials: true,
      })
      .then((res) => {
        setPokemonList(res.data);
        setTotalRows(res.data.length);  
        setCurrentPage(1);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Search failed:", err);
        setLoading(false);
      });
  };
  

  const fetchPokemon = React.useCallback((page = 1, pageSize = rowsPerPage, query = "") => {
    setLoading(true);
    api
      .get("/v1/pokemon", {
        params: {
          page,
          limit: pageSize,
          type: typeFilter || undefined,
          query: query || undefined, 
        },
        withCredentials: true,
      })
      .then((res) => {
        setTotalRows(res.data.total);
        setPokemonList(res.data.data);

        setCurrentPage(res.data.page);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, [typeFilter, rowsPerPage]);

  const fetchAllPokemon = React.useCallback(() => {
    setLoading(true);
    api
      .get("/v1/pokemon/all", { withCredentials: true })
      .then((res) => {
        setAllPokemonList(res.data?.data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to fetch all Pokémon:", err);
        setLoading(false);
      });
  }, []);

  const refreshParty = () => {
    api
      .get("/v1/roster", { withCredentials: true })
      .then((res) => setPartyValue(res.data))
      .catch((err) => console.error("Failed to fetch updated party:", err));
  };

  useEffect(() => {
    if (!isSearching && !favoritesOnly) {
      fetchPokemon(currentPage);
    }
  }, [fetchPokemon, currentPage, isSearching, favoritesOnly]);

  useEffect(() => {
    if (favoritesOnly && !isSearching) {
      setCurrentPage(1);
      fetchAllPokemon();
    }
  }, [favoritesOnly, fetchAllPokemon, isSearching]);

  // Reset to first page when type filter changes (non-search mode)
  useEffect(() => {
    if (favoritesOnly) {
      setCurrentPage(1);
      return;
    }
    if (!isSearching) {
      setCurrentPage(1);
      fetchPokemon(1, rowsPerPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeFilter, favoritesOnly]);
  
  
  

  const handlePartyChange = (pokemonId, targetSlot) => {
    api
      .post(
        "/v1/roster/action",
        {
          pokemon_id: pokemonId,
          target_slot: targetSlot,
        },
        { withCredentials: true },
      )
      .then(() => {
        refreshParty();
        fetchPokemon(currentPage);
      })
      .catch(() => alert("Failed to update Pokémon in your party."));
  };

  const getNextAvailableSlot = () => {
    let nextSlot = 1;
    if (partyValue && partyValue.length > 0) {
      const usedSlots = partyValue.map((p) => p.slot);
      for (let i = 1; i <= 6; i++) {
        if (!usedSlots.includes(i)) {
          nextSlot = i;
          break;
        }
      }
    }
    return nextSlot;
  };

  const baseList = isSearching
    ? sortedPokemonList
    : favoritesOnly
      ? allPokemonList
      : pokemonList;
  const typeFilteredList = favoritesOnly && typeFilter
    ? baseList.filter((mon) =>
        String(mon.name || "").toLowerCase().includes(typeFilter.toLowerCase()),
      )
    : baseList;
  const favoritesFilteredList = favoritesOnly
    ? typeFilteredList.filter((mon) => mon.is_favorite)
    : typeFilteredList;
  const effectiveTotalRows = (isSearching || favoritesOnly)
    ? favoritesFilteredList.length
    : totalRows;
  const totalPages = Math.max(1, Math.ceil(effectiveTotalRows / rowsPerPage));
  const startIndex = effectiveTotalRows === 0
    ? 0
    : (currentPage - 1) * rowsPerPage + 1;
  const endIndex = Math.min(effectiveTotalRows, currentPage * rowsPerPage);
  // For search/favorites mode, paginate client-side
  const displayedList = (isSearching || favoritesOnly)
    ? favoritesFilteredList.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : favoritesFilteredList;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);
  

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Pokémon" breadcrumbItem="" />

          <div className="equipment-container box-shell">
            <div className="equipment-header box-header">
              <h1 className="equipment-title box-title">Box</h1>
              <p className="equipment-subtitle box-subtitle">Manage your stored Pokémon</p>
            </div>

            <div className="inventory-section box-panel">
              <div className="inventory-header box-toolbar">
                <div className="search-container box-search" style={{ flex: '1 1 320px' }}>
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Search Pokémon..."
                    value={search}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearch(value);
                      if (value.trim() === "") {
                        setIsSearching(false);
                        if (favoritesOnly) {
                          fetchAllPokemon();
                        } else {
                          fetchPokemon(1, rowsPerPage);
                        }
                      } else {
                        setIsSearching(true);
                        searchPokemon(value);
                      }
                    }}
                  />
                  <div className="search-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="21 21l-4.35-4.35"></path>
                    </svg>
                  </div>
                </div>

                <select
                  className="form-select box-select"
                  style={{ maxWidth: '200px' }}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <option value="">All Types</option>
                  <option value="Diamond">Diamond</option>
                  <option value="Emerald">Emerald</option>
                  <option value="Golden">Golden</option>
                  <option value="Ruby">Ruby</option>
                  <option value="Sapphire">Sapphire</option>
                  <option value="Shadow">Shadow</option>
                  <option value="Shiny">Shiny</option>
                </select>

                <div className="box-toggle-group">
                  <Button
                    color={favoritesOnly ? "secondary" : "warning"}
                    size="sm"
                    outline={favoritesOnly}
                    onClick={() => {
                      setFavoritesOnly(false);
                      setCurrentPage(1);
                    }}
                  >
                    All
                  </Button>
                  <Button
                    color={favoritesOnly ? "warning" : "secondary"}
                    size="sm"
                    outline={!favoritesOnly}
                    onClick={() => {
                      setFavoritesOnly(true);
                      setCurrentPage(1);
                    }}
                  >
                    Favorites
                  </Button>
                </div>

                <div className="box-rows">
                  <span className="box-rows-label">Rows:</span>
                  <select
                    className="form-select box-select"
                    style={{ width: 100 }}
                    value={rowsPerPage}
                    onChange={(e) => {
                      const newLimit = parseInt(e.target.value, 10);
                      setRowsPerPage(newLimit);
                      setCurrentPage(1);
                      if (!isSearching && !favoritesOnly) fetchPokemon(1, newLimit);
                    }}
                  >
                    <option value={12}>12</option>
                    <option value={20}>20</option>
                    <option value={40}>40</option>
                    <option value={60}>60</option>
                    <option value={100}>100</option>
                  </select>
                </div>
              </div>

              <div className="inventory-grid box-grid">
                {displayedList.map((mon) => {
                  const level = getLevel(mon.totalexp);
                  const hpPercent = getHpPercent(mon);
                  const hpTone = getHpTone(hpPercent);
                  return (
                    <div
                      key={mon.id}
                      className={`inventory-item box-card ${activePokemonId === mon.id ? "is-active" : ""} ${openDropdownCardId === mon.id ? "has-open-dropdown" : ""}`}
                      onClick={(e) => {
                        if (e.target.closest('.box-favorite')) return;
                        setActivePokemonId(mon.id);
                        openStatsModal(mon);
                        setTimeout(() => setActivePokemonId(null), 260);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <button
                        type="button"
                        title={mon.is_favorite ? "Unfavorite" : "Favorite"}
                        onClick={(event) => toggleFavorite(mon, event)}
                        className={`box-favorite ${mon.is_favorite ? "is-active" : ""}`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill={mon.is_favorite ? '#111827' : 'none'}
                          stroke="currentColor"
                          strokeWidth="1.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M12 2l2.9 6.1 6.7.6-5 4.5 1.5 6.6L12 16.8 5.9 19.8 7.4 13.2 2.4 8.7l6.7-.6L12 2z" />
                        </svg>
                      </button>
                      <div className="box-level-pill" style={{ background: getLevelColor(level) }}>
                        Lvl {level.toLocaleString()}
                      </div>
                      <div className="box-sprite">
                        <img
                          src={`/sprites/${mon.name}.png`}
                          alt={mon.nickname || mon.name}
                          className="inventory-item-image box-sprite-img"
                          onError={(event) => {
                            event.currentTarget.onerror = null;
                            event.currentTarget.src = `/icon/${mon.name}.png`;
                          }}
                        />
                      </div>
                      <div className="box-card-body">
                        <div className="inventory-item-name box-name">{mon.nickname || mon.name}</div>
                        <div className="box-types">
                          {mon.type1 ? (
                            <span
                              className="box-type-chip"
                              style={{ background: getTypeColor(mon.type1) }}
                            >
                              {typeLabel(mon.type1)}
                            </span>
                          ) : (
                            <span className="box-type-chip muted">Unknown</span>
                          )}
                          {mon.type2 && (
                            <span
                              className="box-type-chip"
                              style={{ background: getTypeColor(mon.type2) }}
                            >
                              {typeLabel(mon.type2)}
                            </span>
                          )}
                        </div>
                        <div className="box-hp">
                          <div className="box-hp-track">
                            <div
                              className={`box-hp-fill box-hp-fill--${hpTone}`}
                              style={{ width: `${hpPercent}%` }}
                            />
                          </div>
                          <div className="box-hp-text">
                            {mon.max_hp
                              ? `HP ${mon.current_hp ?? 0} / ${mon.max_hp}`
                              : "HP --"}
                          </div>
                        </div>
                      </div>
                      <div
                        className="box-actions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {mon.slot === 0 ? (
                          partyValue && partyValue.length >= 6 ? (
                            <Button color="warning" size="sm" disabled>
                              Party Full
                            </Button>
                          ) : (
                            <Button
                              color="primary"
                              size="sm"
                              onClick={() => handlePartyChange(mon.id, getNextAvailableSlot())}
                            >
                              Add to Party
                            </Button>
                          )
                        ) : (
                          <PartyActionsDropdown
                            row={mon}
                            handlePartyChange={handlePartyChange}
                            onOpenChange={(open) => setOpenDropdownCardId(open ? mon.id : null)}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {displayedList.length === 0 && (
                <div className="box-empty">
                  <p>{loading ? 'Loading…' : 'No Pokémon found'}</p>
                </div>
              )}

              <div className="box-pagination">
                <div className="box-count">
                  Showing {startIndex.toLocaleString()}–{endIndex.toLocaleString()} of {effectiveTotalRows.toLocaleString()}
                </div>
                <div className="box-page-controls">
                  <Button color="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
                    First
                  </Button>
                  <Button color="secondary" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    Prev
                  </Button>
                  <span style={{ color: '#b8c5d6' }}>Page {currentPage} / {totalPages}</span>
                  <Button color="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    Next
                  </Button>
                  <Button color="secondary" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
                    Last
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </div>

     
      <StatsModal
        isOpen={statsModal}
        toggle={toggleStatsModal}
        loading={modalLoading}
        data={modalData}
      />
    </React.Fragment>
  );
};

export default Box;
